/**
 * The pieces every redesigned screen is built from.
 *
 * The references are consistent about a small number of shapes - a page with
 * a title and a subtitle, cards with a quiet border, a row of pill filters, a
 * tab strip with counts - so those are components rather than the same
 * classes copied onto nine screens.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

/**
 * A whole screen: scrolls on its own, capped so the content does not stretch
 * across an ultrawide monitor.
 */
export function Page({ children, wide, className }: {
  children: ReactNode;
  /** Uses the full width instead of the reading measure. */
  wide?: boolean;
  className?: string;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className={cn('mx-auto w-full px-7 py-6', wide ? 'max-w-[1560px]' : 'max-w-[1320px]', className)}>
        {children}
      </div>
    </div>
  );
}

/** The title block at the top of a screen. */
export function PageTitle({ title, note, icon, actions }: {
  title: string;
  note?: string;
  icon?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        {icon ? (
          <span className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-accent-soft text-accent-hi">
            <Icon name={icon} size={20} />
          </span>
        ) : null}
        <div className="min-w-0">
          <h1 className="truncate text-[26px] leading-tight font-extrabold tracking-tight">{title}</h1>
          {note ? <p className="mt-0.5 text-[13px] text-mute">{note}</p> : null}
        </div>
      </div>
      {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** A panel. One border, one radius, no shadow - the reference is flat. */
export function Panel({ children, className, pad = true }: {
  children: ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return (
    <section
      className={cn(
        'rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-1',
        pad && 'p-5',
        className
      )}
    >
      {children}
    </section>
  );
}

/** A heading inside a panel. */
export function PanelTitle({ title, count, action }: {
  title: string;
  count?: number | string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3.5 flex items-center gap-2.5">
      <h2 className="text-[15px] font-bold">{title}</h2>
      {count !== undefined ? (
        <span className="grid h-[20px] min-w-[20px] place-items-center rounded-full bg-surface-3 px-1.5 text-[11px] font-bold text-dim tabular-nums">
          {count}
        </span>
      ) : null}
      <span className="flex-1" />
      {action}
    </div>
  );
}

/**
 * The tab strip the references use: a label, a count, and an underline on the
 * one you are on.
 */
export function Tabs({ tabs, value, onChange }: {
  tabs: { id: string; label: string; count?: number; dot?: boolean }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            'relative flex items-center gap-2 px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors',
            value === t.id ? 'text-ink' : 'text-mute hover:text-dim'
          )}
        >
          {t.dot ? <span className="h-[7px] w-[7px] rounded-full bg-good" /> : null}
          {t.label}
          {t.count !== undefined ? (
            <span
              className={cn(
                'grid h-[19px] min-w-[19px] place-items-center rounded-full px-1.5 text-[11px] font-bold tabular-nums',
                value === t.id ? 'bg-accent text-accent-fg' : 'bg-surface-3 text-mute'
              )}
            >
              {t.count}
            </span>
          ) : null}
          {value === t.id ? (
            <span aria-hidden className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-accent-hi" />
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** A filter pill. Outlined when it is doing something. */
export function Pill({ children, on, icon, onClick }: {
  children: ReactNode;
  on?: boolean;
  icon?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-[38px] flex-none items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] border px-3.5 text-[13px] font-semibold transition-colors',
        on
          ? 'border-accent bg-accent-soft text-accent-hi'
          : 'border-line-soft bg-surface-1 text-dim hover:border-line hover:text-ink'
      )}
    >
      {icon ? <Icon name={icon} size={15} className="flex-none opacity-80" /> : null}
      {children}
    </button>
  );
}

/** Nothing to show, said without apology. */
export function Blank({ icon, title, note, action }: {
  icon?: string;
  title: string;
  note?: string;
  action?: ReactNode;
}) {
  return (
    <div className="grid place-items-center px-6 py-14 text-center">
      <div>
        {icon ? (
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 text-mute">
            <Icon name={icon} size={22} />
          </span>
        ) : null}
        <h3 className="text-[15px] font-bold">{title}</h3>
        {note ? <p className="mt-1 text-[13px] text-mute">{note}</p> : null}
        {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
      </div>
    </div>
  );
}

/** A labelled statistic, as used across the hero blocks. */
export function Stat({ icon, value, label }: { icon?: string; value: ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 px-6 text-center">
      {icon ? <Icon name={icon} size={19} className="text-accent-hi" /> : null}
      <span className="text-[26px] leading-none font-extrabold tabular-nums">{value}</span>
      <span className="text-[12px] text-mute">{label}</span>
    </div>
  );
}
